import { useMemo } from "react";

// Constants for event detection
const GAP_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes gap = new event
const MIN_CLUSTER_SIZE = 1; // Minimum photos to form an event (1 for usability)
const MERGE_THRESHOLD_MS = 10 * 60 * 1000; // Merge events less than 10min apart

// Photos in these statuses are still mid-pipeline (Merovingian → Sati →
// Sentinel → Seraph). They have no `capture_date` yet because EXIF
// extraction hasn't completed; counting them under "photos without
// timestamp" is misleading. Exclude from the no-timestamp tally.
const PRE_INGEST_STATUSES = new Set([
  "ingested",
  "processing",
  "detecting",
  "identifying",
]);

// ---------- Formatting helpers ----------

/**
 * Format a timestamp as a day key (YYYY-MM-DD)
 */
function formatDayKey(ts) {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Format a timestamp as readable time (e.g., "2:15 PM")
 */
function formatTime(ts) {
  return new Date(ts).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a day key as readable date (e.g., "Mon, Jan 15")
 */
function formatDayLabel(dayKey) {
  const d = new Date(dayKey + "T12:00:00");
  return d.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/**
 * Generate a human-readable label for an event based on time of day
 */
function generatePeriodLabel(startTs) {
  const hour = new Date(startTs).getHours();
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  return "Evening";
}

/**
 * Calculate density level based on photo count relative to max
 */
function getDensityLevel(count, maxCount) {
  const ratio = count / maxCount;
  if (ratio >= 0.7) return "high";
  if (ratio >= 0.3) return "medium";
  return "low";
}

// ---------- Processing helpers ----------

/**
 * Extract photos with valid timestamps and sort by time
 */
function extractAndSortPhotos(photos) {
  return photos
    .filter((p) => p.capture_date)
    .map((p) => ({
      photo: p,
      ts: new Date(p.capture_date).getTime(),
    }))
    .filter((p) => Number.isFinite(p.ts))
    .sort((a, b) => a.ts - b.ts);
}

/**
 * Group photos into clusters based on time gaps
 */
function clusterByTimeGaps(photosWithTime, gapThresholdMs) {
  const clusters = [];
  let currentCluster = [photosWithTime[0]];

  for (let i = 1; i < photosWithTime.length; i++) {
    const gap = photosWithTime[i].ts - photosWithTime[i - 1].ts;

    if (gap > gapThresholdMs) {
      clusters.push(currentCluster);
      currentCluster = [];
    }
    currentCluster.push(photosWithTime[i]);
  }

  if (currentCluster.length > 0) {
    clusters.push(currentCluster);
  }

  return clusters;
}

/**
 * Merge small clusters with adjacent ones
 */
function mergeSmallClusters(clusters, minClusterSize, mergeThresholdMs) {
  const mergedClusters = [];

  for (const cluster of clusters) {
    const shouldMerge =
      cluster.length < minClusterSize &&
      mergedClusters.length > 0;

    if (shouldMerge) {
      const lastCluster = mergedClusters[mergedClusters.length - 1];
      const gap = cluster[0].ts - lastCluster[lastCluster.length - 1].ts;

      if (gap < mergeThresholdMs) {
        lastCluster.push(...cluster);
        continue;
      }
    }

    mergedClusters.push(cluster);
  }

  return mergedClusters;
}

/**
 * Convert a cluster to an event object
 */
function createEvent(cluster, eventIndex) {
  const startTs = cluster[0].ts;
  const endTs = cluster[cluster.length - 1].ts;

  return {
    id: `event-${eventIndex}`,
    start: startTs,
    end: endTs,
    count: cluster.length,
    day: formatDayKey(startTs),
    period: generatePeriodLabel(startTs),
    timeRange: `${formatTime(startTs)} - ${formatTime(endTs)}`,
    density: "medium", // Calculated later
  };
}

/**
 * Group events by day
 */
function groupEventsByDay(mergedClusters, minClusterSize) {
  const eventsByDay = new Map();
  let eventIndex = 0;

  for (const cluster of mergedClusters) {
    if (cluster.length < minClusterSize) continue;

    const event = createEvent(cluster, eventIndex++);
    const dayKey = event.day;

    if (!eventsByDay.has(dayKey)) {
      eventsByDay.set(dayKey, []);
    }
    eventsByDay.get(dayKey).push(event);
  }

  return eventsByDay;
}

/**
 * Build final days structure from events by day map
 */
function buildDaysStructure(eventsByDay) {
  const allEvents = [];

  const days = Array.from(eventsByDay.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, events]) => {
      allEvents.push(...events);
      return {
        date: dayKey,
        label: formatDayLabel(dayKey),
        events: events.sort((a, b) => a.start - b.start),
        totalPhotos: events.reduce((sum, e) => sum + e.count, 0),
      };
    });

  return { days, allEvents };
}

/**
 * Calculate and assign density levels to events
 */
function assignDensityLevels(allEvents) {
  const maxEventCount = Math.max(...allEvents.map((e) => e.count), 1);

  for (const event of allEvents) {
    event.density = getDensityLevel(event.count, maxEventCount);
  }

  return maxEventCount;
}

/**
 * Calculate timeline positions for events (0-1 range for CSS)
 */
function calculateTimelinePositions(allEvents, totalStart, totalEnd) {
  const totalRange = totalEnd - totalStart || 1;

  for (const event of allEvents) {
    event.positionStart = (event.start - totalStart) / totalRange;
    event.positionEnd = (event.end - totalStart) / totalRange;

    // Ensure minimum width for visibility
    if (event.positionEnd - event.positionStart < 0.02) {
      event.positionEnd = event.positionStart + 0.02;
    }
  }
}

// ---------- Bucket series (sparkline) ----------

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/**
 * Pick a bucket size for the sparkline given the total time span.
 * Targets ~30–80 buckets across the span so the chart stays readable.
 */
function pickBucketSize(spanMs) {
  if (spanMs <= 0) return 15 * 60 * 1000;
  if (spanMs <= 24 * HOUR) return 15 * 60 * 1000;   // 15 min
  if (spanMs <= 7 * DAY)   return HOUR;             // 1 h
  if (spanMs <= 30 * DAY)  return 4 * HOUR;         // 4 h
  return DAY;                                       // 1 d
}

/**
 * Bucket photo timestamps into a fixed-grid count series.
 *
 * Returns a contiguous array spanning `[start, end]` so the sparkline
 * has a continuous baseline (gaps render as zero, not as missing
 * points). Each bucket's `ts` is its left edge.
 */
function bucketPhotos(photosWithTime, totalStart, totalEnd) {
  const span = Math.max(0, totalEnd - totalStart);
  const bucketMs = pickBucketSize(span);
  // Snap start to bucket boundary so consecutive renders share grid alignment.
  const gridStart = Math.floor(totalStart / bucketMs) * bucketMs;
  const gridEnd = Math.ceil(totalEnd / bucketMs) * bucketMs;
  const n = Math.max(1, Math.round((gridEnd - gridStart) / bucketMs));

  const counts = new Array(n).fill(0);
  for (const p of photosWithTime) {
    const idx = Math.min(n - 1, Math.max(0, Math.floor((p.ts - gridStart) / bucketMs)));
    counts[idx] += 1;
  }

  let maxCount = 0;
  const buckets = new Array(n);
  for (let i = 0; i < n; i += 1) {
    const c = counts[i];
    if (c > maxCount) maxCount = c;
    buckets[i] = { ts: gridStart + i * bucketMs, count: c };
  }

  return { buckets, bucketMs, gridStart, gridEnd, maxCount };
}

// ---------- Main function ----------

/**
 * Detect time events from photos by clustering based on time gaps.
 * Groups continuous shooting sessions into distinct events.
 */
function detectTimeEvents(photos, options = {}) {
  const {
    gapThresholdMs = GAP_THRESHOLD_MS,
    minClusterSize = MIN_CLUSTER_SIZE,
    mergeThresholdMs = MERGE_THRESHOLD_MS,
  } = options;

  // 1. Extract and sort photos with capture dates
  const photosWithTime = extractAndSortPhotos(photos);
  // Only count photos as "without timestamp" once they've cleared the
  // pre-ingest pipeline; otherwise we surface confusing counts for
  // photos that simply haven't been read yet.
  const photosWithoutTime = photos.filter(
    (p) => !p.capture_date && !PRE_INGEST_STATUSES.has(p.status),
  ).length;

  if (photosWithTime.length === 0) {
    return {
      days: [],
      events: [],
      totalSpan: null,
      photosWithoutTime,
      maxEventCount: 0,
      buckets: [],
      bucketMs: 0,
      maxBucketCount: 0,
    };
  }

  // 2. Cluster by time gaps
  const clusters = clusterByTimeGaps(photosWithTime, gapThresholdMs);

  // 3. Merge small clusters
  const mergedClusters = mergeSmallClusters(clusters, minClusterSize, mergeThresholdMs);

  // 4. Group events by day
  const eventsByDay = groupEventsByDay(mergedClusters, minClusterSize);

  // 5. Build final structure
  const { days, allEvents } = buildDaysStructure(eventsByDay);

  // 6. Calculate density levels
  const maxEventCount = assignDensityLevels(allEvents);

  // 7. Calculate timeline positions
  const totalStart = photosWithTime[0].ts;
  const totalEnd = photosWithTime[photosWithTime.length - 1].ts;
  calculateTimelinePositions(allEvents, totalStart, totalEnd);

  // 8. Bucket series for the sparkline (auto-granularity).
  const { buckets, bucketMs, maxCount: maxBucketCount } = bucketPhotos(
    photosWithTime,
    totalStart,
    totalEnd,
  );

  return {
    days,
    events: allEvents,
    totalSpan: { start: totalStart, end: totalEnd },
    photosWithoutTime,
    maxEventCount,
    buckets,
    bucketMs,
    maxBucketCount,
  };
}

// ---------- Hook ----------

/**
 * Hook for computing time events from photos.
 * Memoizes the computation to avoid recalculating on every render.
 */
export function useTimeEvents(photos, options = {}) {
  const { gapThresholdMs, minClusterSize, mergeThresholdMs } = options;
  return useMemo(
    () => detectTimeEvents(photos || [], { gapThresholdMs, minClusterSize, mergeThresholdMs }),
    [photos, gapThresholdMs, minClusterSize, mergeThresholdMs]
  );
}

export { detectTimeEvents, formatTime, formatDayLabel, formatDayKey };
export default useTimeEvents;
